import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BrandLogoPreview } from "@/components/features/brands/BrandLogoPreview";
import { useAuth } from "@/contexts/AuthContext";
import { brandKeys, tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import { listBrands } from "@/services/brandService";

/** Radix Select rejects value="" — sentinel for "no marca". */
const NONE = "__none__";

/**
 * Logo frame inside a select row. Small enough that the row keeps the height
 * of a plain text option — the marca is being picked, not inspected.
 */
const ROW_LOGO_PX = 20;

export interface BrandSelectProps {
  id?: string;
  /** Brand id, or null/"" for none. */
  value: string | null;
  onChange: (brandId: string | null) => void;
  disabled?: boolean;
  /** Label for the "no marca" option; defaults to a generic one. */
  noneLabel?: string;
}

/**
 * Picks a marca, or none.
 *
 * Renders NOTHING when the tenant has no brands — every surface that offers a
 * default brand is meaningless without one, and hiding it here keeps that
 * check out of each caller.
 */
export function BrandSelect({
  id,
  value,
  onChange,
  disabled,
  noneLabel,
}: BrandSelectProps): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: brands = [] } = useQuery({
    queryKey: brandKeys.list(),
    queryFn: listBrands,
  });
  // The tenant's own invoice logo, which a marca with logoImage=null SHARES.
  // Queried here rather than taken as a prop so all three callers — invoice
  // form, client default, API key — cannot drift on what they pass; the query
  // is the same cached one the pages already hold.
  const { data: tenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const fallbackLogoUrl =
    tenant?.invoiceLogoImage ?? tenant?.profileImage ?? null;

  if (brands.length === 0) return null;

  return (
    <Select
      value={value ? value : NONE}
      onValueChange={(v) => onChange(v === NONE ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>
          <span className="flex items-center gap-2">
            {/* The company's own logo: "no marca" still issues as the legal
                entity, so the row should show what that looks like rather
                than an empty slot beside the branded rows. */}
            <span
              className="flex shrink-0 items-center justify-center"
              style={{ width: ROW_LOGO_PX, height: ROW_LOGO_PX }}
            >
              {fallbackLogoUrl ? (
                <img
                  src={fallbackLogoUrl}
                  alt=""
                  className="max-h-full max-w-full object-contain"
                />
              ) : null}
            </span>
            {noneLabel ?? t("brands.selectNone", "Sin marca")}
          </span>
        </SelectItem>
        {brands.map((b) => (
          <SelectItem key={b.id} value={b.id}>
            <span className="flex items-center gap-2">
              {/* BrandLogoPreview, not a plain <img>: it applies the stored
                  crop, so the row shows the logo the PDF prints rather than
                  the untrimmed upload. */}
              <span
                className="flex shrink-0 items-center justify-center"
                style={{ width: ROW_LOGO_PX, height: ROW_LOGO_PX }}
              >
                <BrandLogoPreview
                  brand={b}
                  fallbackLogoUrl={fallbackLogoUrl}
                  framePx={ROW_LOGO_PX}
                />
              </span>
              {b.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
